import { ButtonField, Smokebreak } from '@wk/components-react16';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import OngoingEvaluationItem from '../OngoingEvaluation';
import useOnGoingEvaluationsStore from '@/stores/ongoingEvaluations';
import type { EvaluationStatusResponse, OngoingEvaluation } from '@/types';
import { getEvaluationJobStatus } from '@/services';
import { retry } from '@/lib/retry';

interface OngoingEvaluationsProps {
  isOpen: boolean;
  onClose: () => void;
}

interface EvaluationStatusError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

const OngoingEvaluations: FC<OngoingEvaluationsProps> = ({
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const {
    ongoingEvaluations,
    removeOngoingEvaluation,
    clearAllOngoingEvaluations,
    updateEvaluations,
  } = useOnGoingEvaluationsStore(
    useShallow((state) => ({
      ongoingEvaluations: state.ongoingEvaluations,
      removeOngoingEvaluation: state.removeOngoingEvaluation,
      clearAllOngoingEvaluations: state.clearAllOngoingEvaluations,
      updateEvaluations: state.updateEvaluations,
    })),
  );

  const handleDelete = (jobId: string) => {
    removeOngoingEvaluation(jobId);
  };

  const handleClearAll = () => {
    clearAllOngoingEvaluations();
  };

  const activeEvaluationStatuses: Array<OngoingEvaluation['status']> = [
    'Running',
    'Pending',
  ];
  const sortedOngoingEvaluations = useMemo(() => {
    const getStatusPriority = (status: OngoingEvaluation['status']) =>
      activeEvaluationStatuses.includes(status) ? 0 : 1;

    return [...ongoingEvaluations].sort((left, right) => {
      const priorityDifference =
        getStatusPriority(left.status) - getStatusPriority(right.status);

      if (priorityDifference !== 0) {
        return priorityDifference;
      }

      return (
        new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime()
      );
    });
  }, [ongoingEvaluations]);
  const pollingStateRef = useRef<
    Record<string, { timeoutId?: ReturnType<typeof setTimeout>; inFlight: boolean }>
  >({});
  const ongoingEvaluationsRef = useRef<OngoingEvaluation[]>(ongoingEvaluations);
  ongoingEvaluationsRef.current = ongoingEvaluations;

  const getStatus = async (
    jobId: string,
  ): Promise<{ resp: EvaluationStatusResponse; jobId: string }> => {
    const res = await retry(() => getEvaluationJobStatus(jobId));
    return { resp: res.data, jobId };
  };

  const getErrorMessage = (error: unknown) => {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
      return undefined;
    }

    return (error as EvaluationStatusError).response?.data?.message;
  };

  const removeOldFailedEvaluations = () => {
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const oldFailedEvaluations = ongoingEvaluationsRef.current.filter(
      (evaluation) =>
        evaluation.status === 'Failed' &&
        new Date(evaluation.startedAt) < twentyFourHoursAgo,
    );
    oldFailedEvaluations.forEach((evaluation) => {
      removeOngoingEvaluation(evaluation.jobId);
    });
  };

  const isEvaluationActive = (jobId: string) =>
    ongoingEvaluationsRef.current.some(
      (evaluation) =>
        evaluation.jobId === jobId &&
        activeEvaluationStatuses.includes(evaluation.status),
    );

  const clearPolling = (jobId: string) => {
    const state = pollingStateRef.current[jobId];
    if (state?.timeoutId) {
      clearTimeout(state.timeoutId);
    }
    delete pollingStateRef.current[jobId];
  };

  const scheduleNextPoll = (jobId: string, delay = 3000) => {
    const state = pollingStateRef.current[jobId];

    if (!state) {
      pollingStateRef.current[jobId] = { inFlight: false };
    }

    const nextState = pollingStateRef.current[jobId];

    if (nextState.timeoutId) {
      clearTimeout(nextState.timeoutId);
    }

    nextState.timeoutId = setTimeout(() => {
      pollEvaluationStatus(jobId);
    }, delay);
  };

  const pollEvaluationStatus = async (jobId: string) => {
    const state = pollingStateRef.current[jobId];

    if (!state || state.inFlight) {
      return;
    }

    if (!isEvaluationActive(jobId)) {
      clearPolling(jobId);
      return;
    }

    state.inFlight = true;

    try {
      const { resp } = await getStatus(jobId);
      const data = resp as EvaluationStatusResponse | undefined;
      const targetJobId = data?.jobId ?? jobId;
      const status = data?.status ?? 'Pending';

      updateEvaluations({
        [targetJobId]: {
          status,
          output: data?.output,
        },
      });

      if (status === 'Completed' || status === 'Failed') {
        queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      }
    } catch (err: unknown) {
      updateEvaluations({
        [jobId]: {
          status: 'Failed',
          message: getErrorMessage(err),
        },
      });
    } finally {
      state.inFlight = false;
      removeOldFailedEvaluations();

      if (isEvaluationActive(jobId)) {
        scheduleNextPoll(jobId, 3000);
      } else {
        clearPolling(jobId);
      }
    }
  };

  useEffect(() => {
    const activeEvaluations = ongoingEvaluations.filter((evaluation) =>
      activeEvaluationStatuses.includes(evaluation.status),
    );
    const activeJobIds = new Set(activeEvaluations.map((evaluation) => evaluation.jobId));

    activeEvaluations.forEach((evaluation) => {
      if (!pollingStateRef.current[evaluation.jobId]) {
        pollingStateRef.current[evaluation.jobId] = { inFlight: false };
        scheduleNextPoll(evaluation.jobId, 0);
      }
    });

    Object.keys(pollingStateRef.current).forEach((jobId) => {
      if (!activeJobIds.has(jobId)) {
        clearPolling(jobId);
      }
    });

    removeOldFailedEvaluations();
  }, [ongoingEvaluations]);

  useEffect(() => {
    return () => {
      Object.keys(pollingStateRef.current).forEach((jobId) => {
        clearPolling(jobId);
      });
    };
  }, []);

  return (
    <Smokebreak
      isOpen={isOpen}
      position="right"
      onUserRequest={() => onClose()}
    >
      <h2 slot="smokebreakHeader" className="p-3 font-semibold text-lg">
        Ongoing Evaluations
      </h2>
      <div className="p-3" slot="smokebreakBody">
        {ongoingEvaluations.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No ongoing evaluations
          </div>
        ) : (
          <div className="space-y-2">
            {sortedOngoingEvaluations.map((evaluation) => (
              <OngoingEvaluationItem
                key={evaluation.jobId}
                evaluation={evaluation}
                onDelete={handleDelete}
                onViewResults={() => {
                  onClose();
                }}
              />
            ))}
            {ongoingEvaluations.length > 1 &&
              <div className="flex justify-end">
                <ButtonField mode="text" isDanger>
                  <button onClick={handleClearAll}>Clear All</button>
                </ButtonField>
              </div>}
          </div>
        )}
      </div>
    </Smokebreak>
  );
};

export default OngoingEvaluations;
